import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import DynamicSingleSideTab from "../../components/dynamicSingleSideTab/dynamicSingleSideTab";
import ChevronDown from "../../svg/chevron-down";
import { isProduction as isProductionEnv } from "../../utils/runtimeEnv";

const Configurations = () => {
  const [activeTab, setActiveTab] = useState("");
  const [showNavigation, setShowNavigation] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isProduction = isProductionEnv();

  const sideNavElements = useMemo(() => {
    const elements = [
      { id: "documents-configuration", label: "Documents Configuration" },
      { id: "rpa-configuration", label: "RPA Configuration" },
      { id: "form-configuration", label: "Form Configuration" },
      { id: "pa-form-configuration", label: "PA Form Configuration" },
      {
        id: "medicine-name-configuration",
        label: "Medicine Name Configuration",
      },
      {
        id: "prescription-parsing-configuration",
        label: "Prescription Parsing Configuration",
      },
      { id: "pharma-pa-configuration", label: "Pharma PA Configuration" },
      { id: "credentials-configuration", label: "Credentials Configuration" },
      { id: "client-configuration", label: "Client Configuration" },
      { id: "health-facility", label: "Health Facility Configuration" },
      { id: "provider-list", label: "Provider Configuration" },
      { id: "payer-config", label: "Payer Configuration" },
      { id: "nar-agent-grid", label: "NAR Agent Grid" },
      { id: "historic-nar-config", label: "Historic NAR Config" },
      { id: "nar-configuration", label: "NAR Configuration" },
      {
        id: "email-password-configuration",
        label: "Email Password Configuration",
      },
      { id: "gi-configuration", label: "GI RPA Configuration" },
      { id: "dx-code-config", label: "DX Code Config" },
      ...(isProduction
        ? [{ id: "data-migration", label: "Data Migration" }]
        : []),
      { id: "options-configuration", label: "Options Configuration" },
      {
        id: "agentic-auth-on-file-configuration",
        label: "Agentic Auth Config",
      },
      {
        id: "auth-letter-agent-configuration",
        label: "Auth Letter Payer Config",
      },
      {
        id: "authorization-letter-configuration",
        label: "Authorization Letter Configuration",
      },
      {
        id: "auto-assignment-configuration",
        label: "Auto Assignment Configuration",
      },
    ];
    return elements.sort((a, b) => a.label.localeCompare(b.label));
  }, [isProduction]);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    if (currentPath) {
      const foundElement = sideNavElements.find(
        (element) => element.id === currentPath,
      );
      setActiveTab(foundElement?.id || "");
    }
  }, [location.pathname]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    navigate(tabId);
  };

  return (
    <div className="settings--layout">
      {/* Sidebar */}
      <div
        className={`flex h-full ${
          showNavigation ? "settings--navigation" : "settings--navigation__hide"
        }`}
      >
        <div
          className={`settings--navigation__container flex flex-col overflow-hidden ${
            showNavigation ? "animate-slide-in w-full" : "animate-slide-out w-0"
          }`}
        >
          {/* Title */}
          <div className="settings--navigation__header my-3 w-full truncate pl-2 text-lg font-bold leading-normal text-black">
            Config
          </div>

          {/* Links */}
          <div
            className={`settings--navigation__body flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden`}
          >
            {sideNavElements.map((sideNavElement, index) => (
              <div
                key={sideNavElement.id}
                className={`${
                  showNavigation ? "animate-stagger-in" : "animate-stagger-out"
                }`}
                style={{ transitionDelay: `${10 + index * 50}ms` }}
              >
                <DynamicSingleSideTab
                  id={sideNavElement.id}
                  label={sideNavElement.label}
                  isActive={activeTab === sideNavElement.id}
                  onTabClick={handleTabClick}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Toggle Icon */}
        <div className="settings--navigation__icon relative border border-primaryGray-15">
          <div
            className="left-arrow--icon absolute items-center justify-center rounded-full border bg-white p-1 transition-transform duration-300 ease-in-out hover:scale-105 hover:cursor-pointer"
            style={{
              filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
              left: "-14px",
              top: "22px",
            }}
            onClick={() => setShowNavigation(!showNavigation)}
          >
            <ChevronDown rotate={!showNavigation ? 270 : 90} />
          </div>
        </div>
      </div>

      {/* Main Outlet */}
      <div
        className={`settings--outlet px-3 pt-6 ${
          !showNavigation ? "settings--outlet__hide-left" : ""
        }`}
      >
        <Outlet />
      </div>
    </div>
  );
};

export default Configurations;
